/**
 * Returns the last underscore-delimited segment from a nested selection id.
 */
export const getLastSegment = (nestedId: string): string => {
  const parts = String(nestedId).split('_');
  return parts[parts.length - 1] ?? '';
};

/**
 * Extracts the raw item id from a nested selection id.
 * Supports new "depth:id" segment format by stripping the depth prefix when present.
 * Examples:
 *  - "0:6_1:16_2:3" -> "3"
 *  - "6_16_3" -> "3"
 *  - "42" -> "42"
 */
export const getRawIdFromNestedId = (nestedId: string): string => {
  const last = getLastSegment(nestedId);
  if (last.includes(':')) {
    const segParts = last.split(':');
    return segParts[segParts.length - 1] ?? last;
  }
  return last;
};

/**
 * Returns the display-friendly leaf id from a node id.
 * Handles ids with list prefixes (".") and nested segments ("_" or "depth:id").
 */
export const getDisplayIdFromNodeId = (nodeId: string): string => {
  const lastSegment = getLastSegment(String(nodeId));
  const dotParts = lastSegment.split('.');
  const dotLast = dotParts[dotParts.length - 1] ?? lastSegment;
  if (dotLast.includes(':')) {
    const segParts = dotLast.split(':');
    return segParts[segParts.length - 1] ?? dotLast;
  }
  return dotLast;
};
/**
 * Parses a numeric id from a nested selection id, or returns null if invalid.
 */
export const getNumericIdFromNestedId = (nestedId: string): number | null => {
  const raw = getRawIdFromNestedId(nestedId);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};
